import React from 'react'

import {PlantMapData, useMapCoordinates} from '../../hooks'
import {Bounds, MapAreaConfig} from '../../types'

import {AreaMap} from './AreaMap'
import {AreaRegionOverlay} from './AreaRegionOverlay'
import {BordersOverlay} from './BordersOverlay'
import {PerformanceMapMarker} from './PerformanceMapMarker'
import {ZoomableMapContainer} from './ZoomableMapContainer'

type AreaKilnStatusMapProps = {
  width: number
  height: number
  plantMapData: PlantMapData[]
  mapAreaConfig: MapAreaConfig
  displayBounds: Bounds
  onPlantMarkerClick: (plantId: string) => void
}

export const AreaKilnStatusMap: React.FC<AreaKilnStatusMapProps> = ({
  width,
  height,
  mapAreaConfig,
  plantMapData,
  displayBounds,
  onPlantMarkerClick
}) => {
  const {
    enableZoom = false,
    initialZoom = 1,
    focusPointX,
    focusPointY,
    minZoom = 0.5,
    maxZoom = 4
  } = mapAreaConfig.zoomConfig ?? {}

  const {translate, scale, getMarkerPosition} = useMapCoordinates({
    priorityBounds: mapAreaConfig.priorityBounds,
    plantLocationMap: mapAreaConfig.plantLocationMap,
    displayBounds
  })

  const calculateInitialPan = (): {panX: number; panY: number} => {
    if (!enableZoom || initialZoom <= 1) {
      return {panX: 0, panY: 0}
    }

    const hasFocusPoint = focusPointX !== undefined && focusPointY !== undefined
    const centerX = width / 2
    const centerY = height / 2

    if (hasFocusPoint) {
      const screenFocusX = (focusPointX * scale + translate.x) * initialZoom
      const screenFocusY = (focusPointY * scale + translate.y) * initialZoom
      return {
        panX: centerX - screenFocusX,
        panY: centerY - screenFocusY
      }
    }

    return {
      panX: centerX * (1 - initialZoom),
      panY: centerY * (1 - initialZoom)
    }
  }

  const {panX: initialPanX, panY: initialPanY} = calculateInitialPan()

  const zoomableContent = (
    <>
      {/* Order matters */}
      <AreaRegionOverlay
        width={width}
        height={height}
        scale={scale}
        translate={translate}
        subRegions={mapAreaConfig.subRegions}
        data={plantMapData}
      />
      <BordersOverlay
        bordersPaths={mapAreaConfig.bordersPaths}
        width={width}
        height={height}
        scale={scale}
        translate={translate}
      />
    </>
  )

  return (
    <AreaMap width={width} height={height}>
      <ZoomableMapContainer
        width={width}
        height={height}
        zoomableContent={zoomableContent}
        enableZoom={enableZoom}
        minZoom={minZoom}
        maxZoom={maxZoom}
        initialZoom={initialZoom}
        initialPanX={initialPanX}
        initialPanY={initialPanY}
        zoomStep={0.15}
        renderMarkers={(getZoomedPosition) =>
          plantMapData?.map(({equipments, pxTrendMetaData, plantId, plantName}) => {
            const basePosition = getMarkerPosition(plantId)

            if (!basePosition) {
              return null
            }

            const zoomedPosition = getZoomedPosition(basePosition)

            return (
              <PerformanceMapMarker
                key={plantId}
                equipments={equipments}
                pxTrendMetaData={pxTrendMetaData}
                plantName={plantName}
                coordinate={zoomedPosition}
                onClick={() => onPlantMarkerClick(plantId)}
                dataTestId={`plant-marker-${plantId}`}
              />
            )
          })
        }
      />
    </AreaMap>
  )
}
